import { stripAround } from "../assets/animation.ts";
import { frameId, type AssetManifest } from "../assets/manifest.ts";
import type { PropBehaviour, TileRef } from "../document/types.ts";
import type { EditorCatalogue } from "./catalogue.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import { stripAt } from "./strips.ts";

/**
 * The transition list of the prop inspector (ADR-058, P1.8-T6). A game registers an object type whose
 * transitions name the animation each `plays`; Daedalus lists them and offers, for each animation name, the
 * one-shot strips of the editor's atlas that fit the placed frame, writing the chosen strip's first frame
 * into `PropBehaviour.animations` as the compiler reads it. Nothing here knows what a transition means.
 */

/**
 * One transition as the list shows it. `plays` is the animation name whose strip the author chooses, null
 * for a transition that declares none or keeps its state, since the compiler carries no animation for
 * either. Animations are keyed by name, so transitions naming the same one share its strip; `sharedWith`
 * names the verbs of the others. A client sees only a change of state, so the compiler plays one strip per
 * ordered pair of states, the first declared transition's whose animation the prop names (P1.8-T2 note 2);
 * `sameChangeAs` names the verbs of the other transitions that play a different animation for the same
 * change, whose strips therefore compete for it. `reverse` is present, and true, on a row that plays its
 * strip last frame to first (ADR-058's amendment of 2026-10-03).
 */
export interface TransitionRow {
  readonly verb: string;
  readonly from: string;
  readonly to: string;
  readonly plays: string | null;
  readonly reverse?: true;
  readonly sharedWith: readonly string[];
  readonly sameChangeAs: readonly string[];
}

/**
 * A one-shot strip a transition can play: the frame it starts on, which is what the behaviour stores, its
 * name, and its frames and durations for the count and the preview.
 */
export interface StripChoice {
  readonly frame: TileRef;
  readonly name: string;
  readonly frames: readonly string[];
  readonly durations: readonly number[];
}

/**
 * The animation a transition can choose a strip for: its declared `plays`, unless it keeps its state.
 *
 * @param transition The transition.
 * @param transition.from Its source state.
 * @param transition.to Its target state.
 * @param transition.plays The animation it names, if any.
 * @returns The name, or null.
 */
function playedName(transition: { readonly from: string; readonly to: string; readonly plays?: string }): string | null {
  return transition.from === transition.to || transition.plays === undefined ? null : transition.plays;
}

/**
 * The rows of a type's transition list, in declaration order.
 *
 * @param type The object type.
 * @returns The rows.
 */
export function transitionRows(type: ObjectTypeDefinition): TransitionRow[] {
  return type.transitions.map((transition, index) => {
    const plays = playedName(transition);
    const others = plays === null ? [] : type.transitions.filter((other, j) => j !== index && playedName(other) !== null);
    const sharedWith = others.filter((other) => playedName(other) === plays).map((other) => other.verb);
    const sameChangeAs = others.filter((other) => other.from === transition.from && other.to === transition.to && playedName(other) !== plays).map((other) => other.verb);
    return { verb: transition.verb, from: transition.from, to: transition.to, plays, ...(plays !== null && transition.reverse === true ? { reverse: true as const } : {}), sharedWith, sameChangeAs };
  });
}

/**
 * The state that rests where a transition's strip ends, on its last frame: the state a forward transition
 * enters, and the state a reversed one leaves, since a reversed transition starts on that frame (ADR-058's
 * amendment of 2026-10-03). The resting offer is made for this state, which is how a state that only a
 * reversed transition touches can be rested on its strip.
 *
 * @param transition The transition, or its row.
 * @param transition.from Its source state.
 * @param transition.to Its target state.
 * @param transition.reverse Whether it plays its strip reversed.
 * @returns The state's id.
 */
export function restingStateOf(transition: { readonly from: string; readonly to: string; readonly reverse?: true }): string {
  return transition.reverse === true ? transition.from : transition.to;
}

/**
 * The one-shot strip a frame starts, as the chooser lists it: `stripAt` kept to once strips, with the
 * catalogue's name.
 *
 * @param manifest The editor's asset manifest.
 * @param catalogue The world's catalogue, for the strip's name.
 * @param frame The strip's first frame.
 * @returns The strip, or undefined when the frame starts none that plays once.
 */
export function stripStartingAt(manifest: AssetManifest, catalogue: EditorCatalogue, frame: TileRef): StripChoice | undefined {
  const strip = stripAt(manifest, frame);
  if (strip?.plays !== "once") {
    return undefined;
  }
  const name = catalogue.props.find((p) => p.tileset === frame.tileset && p.tile === frame.tile)?.name ?? `tile ${frame.tile}`;
  return { frame: strip.frame, name, frames: strip.frames, durations: strip.durations };
}

/**
 * Whether a frame has the placed frame's width and height, which is what makes a strip starting or ending on
 * it the placed thing's art rather than another's.
 *
 * @param manifest The editor's asset manifest.
 * @param frame The frame.
 * @param placed The prop's own frame.
 * @returns False when either frame is missing from the manifest or their sizes differ.
 */
function drawnAtSizeOf(manifest: AssetManifest, frame: TileRef, placed: TileRef): boolean {
  const size = manifest.frames[frameId(placed.tileset, null, placed.tile)];
  const rect = manifest.frames[frameId(frame.tileset, null, frame.tile)];
  return size !== undefined && rect !== undefined && rect.w === size.w && rect.h === size.h;
}

/**
 * The strips a transition of a placed prop can play: every one-shot strip the catalogue lists whose first
 * frame has the placed frame's width and height, since a transition is drawn where the state frames are and
 * a strip of another size is another thing's. The placed frame's own sheet comes first, then the rest, each
 * by name.
 *
 * @param manifest The editor's asset manifest.
 * @param catalogue The world's catalogue.
 * @param placed The prop's own frame.
 * @returns The strips.
 */
export function stripChoices(manifest: AssetManifest, catalogue: EditorCatalogue, placed: TileRef): StripChoice[] {
  const choices: StripChoice[] = [];
  for (const entry of catalogue.props) {
    const strip = drawnAtSizeOf(manifest, entry, placed) ? stripStartingAt(manifest, catalogue, entry) : undefined;
    if (strip !== undefined) {
      choices.push(strip);
    }
  }
  const sheet = (choice: StripChoice): number => (choice.frame.tileset === placed.tileset ? 0 : 1);
  return choices.sort((a, b) => sheet(a) - sheet(b) || (a.name < b.name ? -1 : a.name > b.name ? 1 : a.frame.tile - b.frame.tile));
}

/**
 * A behaviour with one animation named or cleared. Clearing the last drops `animations`, so a behaviour
 * whose transitions all cut reads as one that never named a strip.
 *
 * @param behaviour The behaviour.
 * @param name The animation name a transition plays.
 * @param frame The chosen strip's first frame, or undefined for a cut.
 * @returns The new behaviour.
 */
export function withAnimation(behaviour: PropBehaviour, name: string, frame: TileRef | undefined): PropBehaviour {
  const animations: Record<string, TileRef> = { ...behaviour.animations };
  if (frame === undefined) {
    delete animations[name];
  } else {
    animations[name] = frame;
  }
  const { animations: _previous, ...rest } = behaviour;
  return Object.keys(animations).length === 0 ? rest : { ...rest, animations };
}

/**
 * A behaviour with one further state's frame set or cleared, dropping `frames` when none is left.
 *
 * @param behaviour The behaviour.
 * @param stateId The state.
 * @param frame The frame, or undefined to clear it.
 * @returns The new behaviour.
 */
export function withStateFrame(behaviour: PropBehaviour, stateId: string, frame: TileRef | undefined): PropBehaviour {
  const frames: Record<string, TileRef> = { ...behaviour.frames };
  if (frame === undefined) {
    delete frames[stateId];
  } else {
    frames[stateId] = frame;
  }
  const { frames: _previous, ...rest } = behaviour;
  return Object.keys(frames).length === 0 ? rest : { ...rest, frames };
}

/**
 * The strip's last frame as a tile reference in the strip's own tileset.
 *
 * @param strip The strip.
 * @returns The frame, or undefined when the strip is empty or ends outside its tileset's default variant.
 */
function lastFrameOf(strip: StripChoice): TileRef | undefined {
  const last = strip.frames.at(-1);
  const tile = last === undefined ? Number.NaN : Number(last.slice(last.lastIndexOf("/") + 1));
  return Number.isInteger(tile) && tile >= 0 && frameId(strip.frame.tileset, null, tile) === last ? { tileset: strip.frame.tileset, tile } : undefined;
}

/**
 * The frame the state at a strip's end could rest on so the strip joins it without a change of frame: the
 * strip's last, offered when that state (`restingStateOf`: the one a forward transition enters, the one a
 * reversed transition leaves) is a further state that rests elsewhere or on nothing yet. The initial state
 * is offered nothing, since its frame is the placed frame, and neither is a strip whose first or last frame
 * is not drawn at the placed frame's size, since it is another thing's art and the chooser shows it only so
 * the author can clear it. The offer compares frames, not drawings: two frames of different ids that draw
 * alike are still offered, and taking the offer changes nothing on screen.
 *
 * @param manifest The editor's asset manifest, for the frames' sizes.
 * @param placed The prop's own frame.
 * @param type The object type.
 * @param behaviour The prop's behaviour.
 * @param state The state at the strip's end, as `restingStateOf` gives it.
 * @param strip The strip the transition plays, if any.
 * @returns The frame to offer, or undefined.
 */
export function restingFrameOffer(manifest: AssetManifest, placed: TileRef, type: ObjectTypeDefinition, behaviour: PropBehaviour, state: string, strip: StripChoice | undefined): TileRef | undefined {
  if (strip === undefined || state === type.initial || !drawnAtSizeOf(manifest, strip.frame, placed)) {
    return undefined;
  }
  const last = lastFrameOf(strip);
  const resting = behaviour.frames?.[state];
  return last === undefined || !drawnAtSizeOf(manifest, last, placed) || (resting !== undefined && resting.tileset === last.tileset && resting.tile === last.tile) ? undefined : last;
}

/**
 * Whether a state rests on the last frame of a one-shot strip that a state-changing transition plays up to
 * it: forwards into it, or reversed out of it (`restingStateOf`). That is the frame a taken resting offer
 * writes. The inspector then stops suggesting the game's frame for that state, since the suggestion would
 * bring back the change of frame the offer removed.
 *
 * @param manifest The editor's asset manifest.
 * @param type The object type.
 * @param behaviour The prop's behaviour.
 * @param stateId The state.
 * @returns True when the state's frame is such a strip's last.
 */
export function restsOnStripEnd(manifest: AssetManifest, type: ObjectTypeDefinition, behaviour: PropBehaviour, stateId: string): boolean {
  const resting = behaviour.frames?.[stateId];
  if (resting === undefined) {
    return false;
  }
  const at = frameId(resting.tileset, null, resting.tile);
  return type.transitions.some((transition) => {
    const name = restingStateOf(transition) === stateId ? playedName(transition) : null;
    const chosen = name === null ? undefined : behaviour.animations?.[name];
    const animation = chosen === undefined ? undefined : manifest.animations[frameId(chosen.tileset, null, chosen.tile)];
    return animation?.once === true && animation.frames.at(-1) === at;
  });
}

/**
 * How the inspector names a frame: the catalogue's name; for a frame inside a strip, which the catalogue
 * lists only by its first frame, the strip's name and the frame's place in it; otherwise its tile.
 *
 * @param catalogue The world's catalogue.
 * @param manifest The editor's asset manifest.
 * @param frame The frame.
 * @returns The label.
 */
export function frameLabel(catalogue: EditorCatalogue, manifest: AssetManifest, frame: TileRef): string {
  const listed = catalogue.props.find((p) => p.tileset === frame.tileset && p.tile === frame.tile);
  if (listed !== undefined) {
    return listed.name;
  }
  const strip = stripAround(manifest, frameId(frame.tileset, null, frame.tile));
  const head = strip === null ? undefined : catalogue.props.find((p) => p.tileset === frame.tileset && frameId(p.tileset, null, p.tile) === strip.frames[0]);
  return strip === null || head === undefined ? `tile ${frame.tile}` : `${head.name}, frame ${strip.index + 1} of ${strip.frames.length}`;
}
